import { VStack } from '@musinsa/mwms-ds'
import TitleSection from '@/components/content/TitleSection'
import { PageLayout } from '@/components/layout/PageLayout'
import {
  initialFormValues,
  SettlementManagementForm,
  SettlementManagementTable,
  SettlementManagementTableRef,
  SettlementSummary,
  ExcelUploadModal,
} from '@/pages/settlement/SettlementManagement/components'
import {
  useExportExcelSettlementStatistics,
  useExportExcelTemplate,
  useGetSettlementAmountSummary,
  useGetSettlementStatistics,
  useValidateExcelEtcAdjustment,
  useUploadExcelEtcAdjustment,
} from '@/services/settlement/settlementManagement/useSettlementManagementQueries'
import dayjs from 'dayjs'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useRef, useState, useCallback } from 'react'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { CATEGORY_GLOBAL_SETTLEMENT_FLAG, SettlementAmountSummaryReq } from '@/services/settlement/settlementManagement/types'
import { useFeatureFlag } from '@/services/feature/useFeatureQueries'
import { useSettlementConfirm } from '@/pages/settlement/SettlementManagement/hooks/useSettlementConfirm'
import { GRID_SIZE } from '@/utils/grid/grid'
import MRender from '@/utils/modal/MRender'
import { useQueryClient } from '@tanstack/react-query'

function SettlementManagement() {
  const queryClient = useQueryClient()
  const settlementTableRef = useRef<SettlementManagementTableRef>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { reload } = useMenuOnSession()

  const { parsedQs, replaceQuery } = useParsedQuery<SettlementAmountSummaryReq>(initialFormValues)
  const [currentPage, setCurrentPage] = useState(0)

  // LP9182 국가 차원 노출 flag. OFF 면 country 미전송(레거시 동일) — 공유/손상 URL 로 들어와도 BE 에 country 전달 안 함
  const { data: isCategoryGlobalSettlementEnabled } = useFeatureFlag(CATEGORY_GLOBAL_SETTLEMENT_FLAG)

  // LP9182 — useParsedQuery 가 qs.parse(comma:true) 라 손상/공유 URL(?country=KR,JP)이 배열로 파싱될 수 있어 단일 code 로 정규화(BE 단일 KR/JP 만·다중 400, SPEC §3)
  const rawCountry = parsedQs.country as string | string[] | undefined
  const normalizedCountry = Array.isArray(rawCountry) ? rawCountry[0] : rawCountry
  const searchQuery: SettlementAmountSummaryReq = { ...parsedQs, country: isCategoryGlobalSettlementEnabled ? normalizedCountry : undefined }

  const { data: settlementAmounts, removeQuery: settlementAmountsRemoveQuery } = useGetSettlementAmountSummary(searchQuery)

  // FFMPRO-816: cutoverMonth 기반 캘린더 maxDate 계산
  const CUTOVER_DAY = 26
  const today = dayjs()
  const cutoverMonthRaw = settlementAmounts?.cutoverMonth
  const cutoverMonth = cutoverMonthRaw ? dayjs(`${cutoverMonthRaw}-01`).startOf('month') : null
  const maxSelectableDate = ((): Date => {
    if (!cutoverMonth || today.startOf('month').isBefore(cutoverMonth)) {
      return today.toDate()
    }
    return today.date() >= CUTOVER_DAY ? today.add(1, 'month').endOf('month').toDate() : today.toDate()
  })()

  const {
    items: settlementStatistics,
    pageInfo,
    isLoading,
    removeQuery: settlementStatisticsRemoveQuery,
  } = useGetSettlementStatistics({
    ...searchQuery,
    page: currentPage,
    size: GRID_SIZE.SETTLEMENT,
  })

  const { handleSettlementConfirm } = useSettlementConfirm()
  const { onExportData } = useExportExcelSettlementStatistics()
  const { onDownloadTemplate } = useExportExcelTemplate()
  const { mutateAsync: validateExcel } = useValidateExcelEtcAdjustment()
  const { mutateAsync: uploadExcel } = useUploadExcelEtcAdjustment()

  const handleOpenFileSelector = useCallback(() => {
    MRender.destory()
    fileInputRef.current?.click()
  }, [])

  const handleFileSelect = useCallback(
    (file: File) => {
      MRender.show(
        <ExcelUploadModal
          initialFile={file}
          onClose={() => {
            MRender.destory()
          }}
          onValidate={validateExcel}
          onUpload={async (file: File) => {
            await uploadExcel(file)
            queryClient.invalidateQueries({
              queryKey: ['SETTLEMENT_STATISTICS'],
            })
            queryClient.invalidateQueries({
              queryKey: ['SETTLEMENT_SUMMARY'],
            })
            reload()
          }}
          onRetry={handleOpenFileSelector}
          onDownloadTemplate={onDownloadTemplate}
        />,
      )
    },
    [queryClient, reload, validateExcel, uploadExcel, handleOpenFileSelector],
  )

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)

      e.target.value = ''
    }
  }

  const bulkRegisterAdjustmentOptions = [
    {
      label: '엑셀 업로드',
      onClick: handleOpenFileSelector,
    },
    {
      label: '엑셀 템플릿 다운로드',
      onClick: onDownloadTemplate,
    },
  ]

  const handleSearch = (formValues: SettlementAmountSummaryReq) => {
    settlementTableRef.current?.clearSelection()
    replaceQuery(formValues)
    setCurrentPage(0)
    settlementAmountsRemoveQuery()
    settlementStatisticsRemoveQuery()
    MenuStorage.menuOnSession.setSearch(formValues)
    reload()
  }

  return (
    <PageLayout gap={16}>
      <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFileInputChange} />
      <VStack gap={12}>
        <TitleSection title="MFS 파트너 정산관리" />
        <SettlementManagementForm isLoading={isLoading} defaultValues={searchQuery} maxDate={maxSelectableDate} onSearch={handleSearch} />
      </VStack>
      <SettlementSummary
        title={`${dayjs(settlementAmounts ? settlementAmounts.settlementMonth : parsedQs.settlementMonth).month() + 1}월 정산 총액`}
        settlementAmounts={settlementAmounts}
      />
      <SettlementManagementTable
        rows={settlementStatistics}
        pageInfo={pageInfo}
        onChangePage={setCurrentPage}
        onExportData={() => onExportData({ settlementMonth: parsedQs.settlementMonth, country: searchQuery.country })}
        onSettlementConfirm={(ids) => handleSettlementConfirm({ settlementMonth: parsedQs.settlementMonth, partnerIds: ids })}
        bulkRegisterAdjustmentOptions={bulkRegisterAdjustmentOptions}
      />
    </PageLayout>
  )
}

export default SettlementManagement
